import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useInvestorInvestorInvitationResend } from "@vega-api/generated/auth-invitation-investor-investor-v1/auth-invitation-investor-investor-v1";
import { useInvitationResend } from "@vega-api/generated/auth-invitation-v1/auth-invitation-v1";
import { useWorkbenchInvestorInvitationResend } from "@vega-api/generated/auth-invitation-workbench-investor-v1/auth-invitation-workbench-investor-v1";
import { useWorkbenchWorkbenchInvitationResend } from "@vega-api/generated/auth-invitation-workbench-workbench-v1/auth-invitation-workbench-workbench-v1";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { formatFullName } from "@vega-core/utils/formatString";
import { isTruthy } from "@vega-core/utils/isTruthy";

// Which portal-pair resend endpoint to use for the V2 flow. The investor portal
// (Associated users) invites investor-to-investor; the GP/workbench surfaces
// invite workbench-to-investor. Defaults to workbench-investor so existing
// GP callers need no change.
type InvitationVariant =
  | "investor-investor"
  | "workbench-investor"
  | "workbench-workbench";

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  showDescription?: boolean;
  onResendSuccess?: (userId: string) => void;
  invitationVariant?: InvitationVariant;
};

export const ResendInviteModal = ({
  open,
  onClose,
  user,
  showDescription = true,
  onResendSuccess,
  invitationVariant = "workbench-investor",
}: Props) => {
  const showToast = useToast();
  const { featureFlag } = useFeatureFlag();

  const isInvitationV2 = featureFlag[FeatureFlag.GP_AUTH_INVITATION_V2];

  const resendMutationOptions = {
    mutation: {
      onSuccess: () => {
        onClose();
        showToast({
          title: m.userManagement_resend_invite_success_toast(),
          variant: "success",
        });
        onResendSuccess?.(user.id);
      },
      onError: (error: ErrorType<unknown>) => {
        const { title, description } = getErrorMessage(error, {
          defaultMessage: m.userManagement_invite_error(),
        });
        showToast({
          title,
          description,
          variant: "destructive",
        });
      },
    },
  };

  // All hooks are called unconditionally (stable hook order); the flag and the
  // variant only select which resend mutation is used.
  const legacyResend = useInvitationResend(resendMutationOptions);
  const workbenchInvestorResend = useWorkbenchInvestorInvitationResend(
    resendMutationOptions,
  );
  const investorInvestorResend = useInvestorInvestorInvitationResend(
    resendMutationOptions,
  );
  const workbenchWorkbenchResend = useWorkbenchWorkbenchInvitationResend(
    resendMutationOptions,
  );

  const isWorkbenchWorkbench = invitationVariant === "workbench-workbench";

  const resendByVariant = {
    "investor-investor": investorInvestorResend,
    "workbench-investor": workbenchInvestorResend,
    "workbench-workbench": workbenchWorkbenchResend,
  };

  const { mutate: resendInvitation, isPending } =
    isInvitationV2 || isWorkbenchWorkbench
      ? resendByVariant[invitationVariant]
      : legacyResend;

  const handleResend = () => {
    resendInvitation({ params: { invitedUserId: user.id } });
  };

  const fullName = formatFullName({
    firstName: user.firstName,
    lastName: user.lastName,
  }).trim();
  const hasName = isTruthy(fullName);
  const displayName = hasName ? fullName : user.email;

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: handleResend,
        mainActionText: m.userManagement_resend_invite_button(),
        secondaryAction: onClose,
        secondaryActionText: m.btn_cancel(),
        secondaryActionVariant: "tertiary",
        isLoading: isPending,
      }}
      dataTestId="resend-invite-modal"
      defaultWidth
      description={
        showDescription
          ? m.common_resend_invite_description({ email: user.email })
          : undefined
      }
      fluidHeight
      open={open}
      setOpen={onClose}
      title={m.userManagement_resend_invite_button()}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4 rounded-containerCorner border border-border/secondary bg-background/card p-4">
          <div className="flex min-w-0 flex-col gap-1">
            {hasName ? (
              <div className="body-base-medium text-text/primary truncate">
                {displayName}
              </div>
            ) : null}
            <div className="body-small-regular text-text/secondary truncate">
              {user.email}
            </div>
          </div>
        </div>
      </div>
    </ActionaryModal>
  );
};
